import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { activitiesAPI } from '../../../utils/api';
import { format } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';
import { getErrorMessage } from '../../../utils/errorTranslation';

const HistoryMetrics = ({ filters, showToast }) => {
  const { t, currentLanguage } = useLanguage();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalActivities, setTotalActivities] = useState(0);

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      const response = await activitiesAPI.getActivities({ page: 1, limit: 5 });
      const data = response.data;
      setActivities(data.activities || []);
      setTotalActivities(data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching activities:', error);
      if (showToast) {
        showToast(getErrorMessage(error, t, t('dashboard.analytics.error.fetchFailed')), 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  const getLocale = () => {
    switch (currentLanguage) {
      case 'en':
        return enUS;
      case 'es':
        return es;
      default:
        return fr;
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities, filters]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 h-full min-h-[320px]"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('dashboard.analytics.history.title')}</h3>
          <p className="text-sm text-gray-500">{t('dashboard.analytics.history.subtitle')}</p>
        </div>
        <Activity className="h-6 w-6 text-primary-600" />
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">{t('common.loading')}</div>
      ) : activities.length === 0 ? (
        <div className="text-sm text-gray-500">{t('dashboard.analytics.history.noData')}</div>
      ) : (
        <div className="space-y-3">
          {activities.map((activity) => (
            <div key={activity.id} className="text-sm text-gray-600">
              <div className="font-medium text-gray-800">{activity.action_description || activity.description}</div>
              <div className="text-xs text-gray-400">
                {activity.user_name || activity.user || ''} · {format(new Date(activity.created_at || activity.date), 'P', { locale: getLocale() })}
              </div>
            </div>
          ))}
          <div className="text-xs text-gray-500">
            {t('dashboard.analytics.history.totalActions')}: {totalActivities}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default HistoryMetrics;
